import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { motion, useReducedMotion } from "framer-motion";
import { toast } from "sonner";
import {
    ArrowRight, Check, CheckCircle2, Loader2, User, Building2, Mail, Phone, Calendar,
    MapPin, Volume2, Play, Wallet, CreditCard, ShieldCheck, Sparkles,
} from "lucide-react";
import { api, useAuth } from "@/lib/auth";
import { NoxnLogo } from "@/components/noxn/NoxnLogo";
import { usePageMeta } from "@/hooks/usePageMeta";
import { PortalShell } from "@/components/account/PortalShell";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SS_KEY = "noxn.book.v3";
const INK = "#1E3A6F";
const ACCENT = "#FF5A00";
const CITIES = ["Brisbane", "Sydney", "Melbourne", "Perth", "Adelaide"];

const cents = (c) => (c == null ? "—" : `$${(c / 100).toFixed(2)}`);
const dollars = (c) => (c == null ? "$0.00" : `$${(c / 100).toFixed(2)}`);

const emptyValues = () => ({
    business_name: "",
    contact_name: "",
    email: "",
    phone: "",
    launch_date: "",
    campaign_goal: "awareness",
    cities: [],
    audio: false,
    notes: "",
    plays: 200,
    screen_id: null,
    screen_name: "",
    screen_city: "",
    screen_play_weight: 1,
});

/**
 * Compact map-style preview tile for a specific billboard screen.
 * Renders a soft top-down dot grid with an orange pin at the screen's
 * relative lat/lng inside the city bounding box for the given screen,
 * so guests get a visual sense of *where* the billboard sits before
 * picking it — instead of just choosing a city.
 */
const ScreenTile = ({ screen, active, onPick }) => {
    // Simple relative dot position — deterministic per screen coord.
    const seedX = screen.lng ? ((Math.abs(screen.lng) * 137) % 100) / 100 : 0.5;
    const seedY = screen.lat ? ((Math.abs(screen.lat) * 91) % 100) / 100 : 0.5;
    const px = Math.min(0.85, Math.max(0.15, seedX));
    const py = Math.min(0.85, Math.max(0.15, seedY));
    return (
        <motion.button
            type="button"
            onClick={() => onPick(screen)}
            whileHover={{ y: -3 }}
            whileTap={{ scale: 0.98 }}
            data-testid={`book-screen-${screen.id}`}
            className="group text-left rounded-2xl border overflow-hidden transition-colors bg-white"
            style={{
                borderColor: active ? ACCENT : `${INK}22`,
                boxShadow: active
                    ? `0 12px 24px -14px ${ACCENT}55`
                    : `0 6px 18px -12px ${INK}22`,
            }}
        >
            <div
                className="relative h-24"
                style={{
                    backgroundColor: active ? `${ACCENT}12` : `${INK}08`,
                    backgroundImage: `radial-gradient(rgba(30,58,111,0.16) 1px, transparent 1px)`,
                    backgroundSize: "10px 10px",
                }}
            >
                {/* map pin */}
                <span
                    className="absolute w-4 h-4 rounded-full ring-4 transition-all group-hover:scale-125"
                    style={{
                        left: `calc(${px * 100}% - 8px)`,
                        top: `calc(${py * 100}% - 8px)`,
                        backgroundColor: ACCENT,
                        boxShadow: `0 0 0 6px ${ACCENT}22`,
                    }}
                />
                {active && (
                    <span
                        className="absolute top-2 right-2 inline-flex items-center gap-1 text-[9px] font-mono-tech uppercase tracking-[0.24em] px-2 py-1 rounded-full"
                        style={{ backgroundColor: ACCENT, color: "#FFFFFF" }}
                    >
                        <Check size={9} /> Picked
                    </span>
                )}
            </div>
            <div className="p-3">
                <div className="font-display text-sm font-medium truncate" style={{ color: INK }}>
                    {screen.name}
                </div>
                <div className="text-[11px] mt-0.5 flex items-center gap-1" style={{ color: `${INK}70` }}>
                    <MapPin size={10} style={{ color: ACCENT }} />
                    {screen.city}{screen.format ? ` · ${screen.format}` : ""}
                </div>
            </div>
        </motion.button>
    );
};

export default function Book() {
    usePageMeta({
        title: "Book a screen · NOXN",
        description: "Reserve a digital billboard on Australia's largest small-brand out-of-home network.",
    });
    const { user } = useAuth();
    const navigate = useNavigate();
    const [params] = useSearchParams();
    const reduce = useReducedMotion();

    const [values, setValues] = useState(() => {
        try {
            const raw = sessionStorage.getItem(SS_KEY);
            if (raw) return { ...emptyValues(), ...JSON.parse(raw) };
        } catch (e) { /* ignore */ }
        const v = emptyValues();
        if (user?.email) v.email = user.email;
        if (user?.name) v.contact_name = user.name;
        return v;
    });
    const [tiers, setTiers] = useState([]);
    const [minPurchase, setMinPurchase] = useState(200);
    const [quote, setQuote] = useState(null);
    const [balance, setBalance] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [assessmentId, setAssessmentId] = useState(null);
    const [allScreens, setAllScreens] = useState([]);

    useEffect(() => {
        try { sessionStorage.setItem(SS_KEY, JSON.stringify(values)); } catch (e) { /* ignore */ }
    }, [values]);

    // Load tiers + prefill screen if provided
    useEffect(() => {
        axios.get(`${API}/plays/tiers/active`).then(({ data }) => {
            setTiers(data.tiers || []);
            setMinPurchase(data.min_purchase || 200);
            setValues((v) => v.plays < (data.min_purchase || 200) ? { ...v, plays: data.min_purchase || 200 } : v);
        }).catch(() => { });
    }, []);

    useEffect(() => {
        const sidSingle = params.get("screen");
        const sidsMulti = params.get("screens");
        if (!sidSingle && !sidsMulti) return;
        axios.get(`${API}/screens/public/map`).then(({ data }) => {
            const all = Array.isArray(data) ? data : [];
            // Multi-screen selection from Screen Map: preselect the first one
            // in the primary screen field and union all cities. Additional
            // screens land in `notes` so ops can allocate them at approval.
            if (sidsMulti) {
                const ids = sidsMulti.split(",").filter(Boolean);
                const picks = ids.map((id) => all.find((x) => x.id === id)).filter(Boolean);
                if (!picks.length) return;
                const first = picks[0];
                const cities = [...new Set(picks.map((p) => p.city))];
                const names = picks.map((p) => `${p.name} (${p.city})`);
                setValues((v) => ({
                    ...v,
                    screen_id: picks.length === 1 ? first.id : null,
                    screen_name: picks.length === 1 ? first.name : "",
                    screen_city: picks.length === 1 ? first.city : "",
                    screen_play_weight: Number(first.play_weight) || 1,
                    cities: [...new Set([...v.cities, ...cities])],
                    notes: picks.length > 1
                        ? [v.notes, `Screens requested: ${names.join(", ")}`].filter(Boolean).join("\n")
                        : v.notes,
                }));
                return;
            }
            const s = all.find((x) => x.id === sidSingle);
            if (s) {
                setValues((v) => ({
                    ...v,
                    screen_id: s.id,
                    screen_name: s.name,
                    screen_city: s.city,
                    screen_play_weight: Number(s.play_weight) || 1,
                    cities: v.cities.includes(s.city) ? v.cities : [...v.cities, s.city],
                }));
            }
        }).catch(() => { });
    }, [params]);

    // Load all public screens once so Section 2 can show a mini map preview
    // as soon as the guest picks a city.
    useEffect(() => {
        axios.get(`${API}/screens/public/map`)
            .then(({ data }) => setAllScreens(Array.isArray(data) ? data : []))
            .catch(() => setAllScreens([]));
    }, []);

    useEffect(() => {
        if (!user) { setBalance(null); return; }
        api.get("/plays/balance").then(({ data }) => setBalance(data.balance || 0)).catch(() => setBalance(0));
    }, [user]);

    // Live quote
    useEffect(() => {
        if (!values.plays || values.plays < minPurchase) { setQuote(null); return; }
        const ctrl = new AbortController();
        axios.post(`${API}/plays/quote`, { plays: Number(values.plays), user_id: user?.id }, { signal: ctrl.signal })
            .then(({ data }) => setQuote(data))
            .catch(() => setQuote(null));
        return () => ctrl.abort();
    }, [values.plays, minPurchase, user?.id]);

    const totalCents = quote?.total_cents ?? 0;
    const gstCents = Math.round(totalCents * 0.10);
    const grandCents = totalCents + gstCents;
    const balanceSafe = balance ?? 0;
    const shortfall = Math.max(0, Number(values.plays || 0) - balanceSafe);

    const errors = useMemo(() => {
        const e = {};
        if (!values.business_name.trim()) e.business_name = "Required";
        if (!values.contact_name.trim()) e.contact_name = "Required";
        if (!values.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.email)) e.email = "Enter a valid email";
        if (!values.launch_date) e.launch_date = "Pick a launch date";
        if (!values.cities.length) e.cities = "Choose at least one city";
        if (!values.plays || values.plays < minPurchase) e.plays = `Minimum ${minPurchase.toLocaleString()} plays`;
        return e;
    }, [values, minPurchase]);
    const canSubmit = Object.keys(errors).length === 0;

    const createAssessment = useCallback(async () => {
        const payload = {
            form_category: "cat2",
            campaign_goal: values.campaign_goal,
            services: ["ad_space"],
            ad_space: {
                screen_id: values.screen_id,
                screen_name: values.screen_name,
                screen_city: values.screen_city,
                audio: !!values.audio,
                notes: values.notes || null,
                cities: values.cities,
            },
            plays: Number(values.plays),
            launch_date: values.launch_date,
            business_name: values.business_name.trim(),
            contact_name: values.contact_name.trim(),
            email: values.email.trim(),
            phone: values.phone.trim() || null,
            notes: values.notes || null,
        };
        const headers = user ? { Authorization: `Bearer ${localStorage.getItem("noxn.token") || ""}` } : {};
        const { data } = await axios.post(`${API}/assessments`, payload, { headers, withCredentials: true });
        return data;
    }, [values, user]);

    const formatError = (e) => {
        const d = e?.response?.data?.detail;
        if (Array.isArray(d)) return d.map((x) => x?.msg || "Invalid field").join(" · ");
        if (typeof d === "string") return d;
        if (d && typeof d === "object" && d.detail) return String(d.detail);
        return "Could not submit your booking.";
    };

    // Guest submit — creates the assessment and kicks straight to Stripe
    // hosted checkout for the fastest possible book → paid flow.
    // On success Stripe redirects back to /c/:token?paid=1.
    const submitAsGuest = async () => {
        if (!canSubmit) return;
        setSubmitting(true);
        try {
            const data = await createAssessment();
            sessionStorage.removeItem(SS_KEY);
            try {
                const { data: chk } = await axios.post(
                    `${API}/guest/campaign/${data.guest_access_token}/checkout`,
                    { origin_url: window.location.origin },
                );
                if (chk?.checkout_url) {
                    window.location.href = chk.checkout_url;
                    return;
                }
            } catch (e) {
                // Fall through to the status page — guest can retry payment there.
                toast.info("Booking saved — continue to payment on the next screen.");
            }
            navigate(`/c/${data.guest_access_token}`, { state: { justCreated: true } });
        } catch (e) {
            toast.error(formatError(e));
        } finally { setSubmitting(false); }
    };

    // Authed submit — creates assessment then confirms with balance (or triggers top-up).
    const submitAsUser = async () => {
        if (!canSubmit) return;
        setSubmitting(true);
        try {
            const data = assessmentId ? { id: assessmentId } : await createAssessment();
            if (!assessmentId) setAssessmentId(data.id);
            const needed = Number(values.plays || 0);
            if (user.role === "super_admin") {
                await api.post("/campaigns/confirm-as-super-admin", { assessment_id: data.id });
                toast.success("Campaign confirmed — payment bypassed (Super Admin).");
                sessionStorage.removeItem(SS_KEY);
                navigate(`/dashboard?campaign=${data.id}`);
                return;
            }
            if (balanceSafe >= needed) {
                await api.post("/campaigns/confirm-with-balance", { assessment_id: data.id });
                toast.success("Campaign confirmed. Watch your inbox for details.");
                sessionStorage.removeItem(SS_KEY);
                navigate(`/account?campaign=${data.id}`);
                return;
            }
            // top up shortfall
            const topupPlays = Math.max(minPurchase, shortfall);
            const { data: chk } = await api.post("/plays/checkout", {
                plays: topupPlays,
                origin_url: window.location.origin,
            });
            if (chk.checkout_url) window.location.href = chk.checkout_url;
        } catch (e) {
            toast.error(formatError(e));
        } finally { setSubmitting(false); }
    };

    const onSubmit = () => (user ? submitAsUser() : submitAsGuest());

    const toggleCity = (c) => setValues((v) => ({
        ...v,
        cities: v.cities.includes(c) ? v.cities.filter((x) => x !== c) : [...v.cities, c],
    }));
    const setPlays = (n) => setValues((v) => ({ ...v, plays: Math.max(0, Math.floor(Number(n) || 0)) }));

    return (
        <PortalShell title="Book a screen" mainClassName="p-0 bg-white text-[#1E3A6F]">
            <div className="min-h-screen bg-white font-body" style={{ color: INK }} data-testid="book-page">
                {/* Standalone header only shown when the persistent portal shell is
                    NOT wrapping this page (i.e. guests + non-client staff). */}
                {!user && <BookHeader authed={!!user} />}

            <main className="mx-auto max-w-6xl px-6 lg:px-10 pt-32 pb-32">
                {/* Intro */}
                <motion.div
                    initial={reduce ? {} : { opacity: 0, y: 14 }}
                    animate={reduce ? {} : { opacity: 1, y: 0 }}
                    transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
                    className="mb-14 max-w-2xl"
                >
                    <div className="text-[11px] uppercase tracking-[0.28em] mb-4" style={{ color: `${INK}80` }}>
                        {user ? "Book a screen" : "Guest booking — no account required"}
                    </div>
                    <h1 className="font-display tracking-[-0.03em] text-4xl sm:text-5xl leading-[1.05] font-medium" style={{ color: INK }}>
                        {user
                            ? "One screen, your artwork, one page."
                            : "Book a screen without an account."}
                    </h1>
                    <p className="mt-5 text-[15px] leading-relaxed" style={{ color: `${INK}99` }}>
                        Fill in the details below — we&apos;ll confirm by email within one business hour with your artwork checklist and payment link.{" "}
                        {!user && (
                            <>Prefer to save your bookings? <Link to="/signup" className="underline" style={{ textDecorationColor: `${INK}40` }}>Create an account</Link>.</>
                        )}
                    </p>
                </motion.div>

                <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-10">
                    <div className="space-y-14">
                        {/* SECTION 1 — Contact */}
                        <Section n={1} title="Who&rsquo;s this for?" reduce={reduce}>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                                <Input testid="book-business" icon={Building2} label="Business name" value={values.business_name} onChange={(x) => setValues({ ...values, business_name: x })} placeholder="Acme Coffee Co." error={errors.business_name} />
                                <Input testid="book-contact" icon={User} label="Contact name" value={values.contact_name} onChange={(x) => setValues({ ...values, contact_name: x })} placeholder="Jane Smith" error={errors.contact_name} />
                                <Input testid="book-email" icon={Mail} label="Email" type="email" value={values.email} onChange={(x) => setValues({ ...values, email: x })} placeholder="jane@acme.com" error={errors.email} />
                                <Input testid="book-phone" icon={Phone} label="Phone (optional)" value={values.phone} onChange={(x) => setValues({ ...values, phone: x })} placeholder="+61 4xx xxx xxx" />
                            </div>
                        </Section>

                        {/* SECTION 2 — Screens */}
                        <Section n={2} title="Where should it run?" reduce={reduce}>
                            {values.screen_id ? (
                                <div className="p-4 rounded-2xl border" style={{ borderColor: `${ACCENT}66`, backgroundColor: `${ACCENT}0d` }} data-testid="book-preselected-screen">
                                    <div className="flex items-center gap-2 text-sm">
                                        <MapPin size={14} style={{ color: ACCENT }} />
                                        <strong>{values.screen_name}</strong> · {values.screen_city}
                                    </div>
                                    <button
                                        onClick={() => setValues({ ...values, screen_id: null, screen_name: "", screen_city: "", screen_play_weight: 1 })}
                                        className="mt-2 text-[12px] underline"
                                        style={{ color: `${INK}99` }}
                                        data-testid="book-clear-screen"
                                    >
                                        Clear this screen and choose by city instead
                                    </button>
                                </div>
                            ) : (
                                <>
                                    <div className="text-[11px] uppercase tracking-[0.28em] mb-3" style={{ color: `${INK}70` }}>Cities · pick one or more</div>
                                    <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
                                        {CITIES.map((c) => {
                                            const active = values.cities.includes(c);
                                            return (
                                                <motion.button
                                                    key={c}
                                                    type="button"
                                                    onClick={() => toggleCity(c)}
                                                    whileHover={{ y: -2 }}
                                                    whileTap={{ scale: 0.98 }}
                                                    data-testid={`book-city-${c.toLowerCase()}`}
                                                    className="p-4 rounded-2xl border text-left transition-colors"
                                                    style={{
                                                        borderColor: active ? ACCENT : `${INK}20`,
                                                        backgroundColor: active ? `${ACCENT}12` : "#FFFFFF",
                                                        color: active ? INK : `${INK}CC`,
                                                    }}
                                                >
                                                    <div className="font-display text-base font-medium">{c}</div>
                                                    {active && (
                                                        <div className="mt-1 flex items-center gap-1 text-[11px]" style={{ color: ACCENT }}>
                                                            <Check size={11} /> Selected
                                                        </div>
                                                    )}
                                                </motion.button>
                                            );
                                        })}
                                    </div>
                                    {errors.cities && <div className="mt-2 text-xs text-red-500">{errors.cities}</div>}

                                    {/* Mini map preview — screens matching the selected cities */}
                                    {values.cities.length > 0 && allScreens.length > 0 && (
                                        <div className="mt-6" data-testid="book-screen-preview">
                                            <div className="flex items-baseline justify-between mb-3">
                                                <div className="text-[11px] uppercase tracking-[0.28em]" style={{ color: `${INK}70` }}>
                                                    Screens in your cities · pick one to lock it in
                                                </div>
                                                <Link to="/map" className="text-[11px] underline" style={{ color: `${INK}75`, textDecorationColor: `${INK}40` }}>
                                                    Full map →
                                                </Link>
                                            </div>
                                            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                                                {allScreens
                                                    .filter((s) => s.lat && s.lng && values.cities.includes(s.city))
                                                    .slice(0, 6)
                                                    .map((s) => (
                                                        <ScreenTile
                                                            key={s.id}
                                                            screen={s}
                                                            active={values.screen_id === s.id}
                                                            onPick={(sc) => setValues((v) => ({
                                                                ...v,
                                                                screen_id: sc.id,
                                                                screen_name: sc.name,
                                                                screen_city: sc.city,
                                                                screen_play_weight: Number(sc.play_weight) || 1,
                                                            }))}
                                                        />
                                                    ))}
                                            </div>
                                            {allScreens.filter((s) => values.cities.includes(s.city)).length === 0 && (
                                                <div className="text-[12px] mt-3" style={{ color: `${INK}70` }}>
                                                    We&apos;ll match you to available screens in {values.cities.join(", ")} — no need to pick one now.
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    <div className="mt-4 text-[12px]" style={{ color: `${INK}75` }}>
                                        We&apos;ll match you to available screens in these cities.{" "}
                                        <Link to="/map" className="underline" style={{ textDecorationColor: `${INK}40` }}>
                                            Or pick a specific screen from the map →
                                        </Link>
                                    </div>
                                </>
                            )}
                        </Section>

                        {/* SECTION 3 — Dates */}
                        <Section n={3} title="When do you want to launch?" reduce={reduce}>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                                <Input testid="book-launch" icon={Calendar} label="Launch date" type="date" value={values.launch_date} onChange={(x) => setValues({ ...values, launch_date: x })} error={errors.launch_date} />
                                <SelectField testid="book-goal" icon={Sparkles} label="Campaign goal" value={values.campaign_goal} onChange={(x) => setValues({ ...values, campaign_goal: x })} options={[
                                    { v: "awareness", l: "Brand awareness" },
                                    { v: "promo", l: "Sale or promotion" },
                                    { v: "event", l: "Event countdown" },
                                    { v: "recruitment", l: "Recruitment" },
                                    { v: "listing", l: "Property / listing" },
                                    { v: "other", l: "Other" },
                                ]} />
                            </div>
                        </Section>

                        {/* SECTION 4 — Plays */}
                        <Section n={4} title="How many plays?" reduce={reduce}>
                            <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-4 items-center">
                                <div className="flex items-stretch gap-2 rounded-2xl border overflow-hidden" style={{ borderColor: `${INK}25` }}>
                                    <button onClick={() => setPlays(values.plays - 100)} data-testid="book-plays-dec" className="w-14 text-xl transition-colors hover:bg-slate-50" aria-label="Decrease">−</button>
                                    <input
                                        type="number" min={0} step={100} value={values.plays}
                                        onChange={(e) => setPlays(e.target.value)} data-testid="book-plays-input"
                                        className="flex-1 text-center font-display text-3xl font-medium bg-transparent outline-none px-3 py-4"
                                        style={{ color: INK }}
                                    />
                                    <button onClick={() => setPlays(Number(values.plays) + 100)} data-testid="book-plays-inc" className="w-14 text-xl transition-colors hover:bg-slate-50" aria-label="Increase">+</button>
                                </div>
                                <div className="text-[12px]" style={{ color: `${INK}80` }}>
                                    Minimum <strong>{minPurchase.toLocaleString()}</strong> plays.
                                    {user && balance !== null && (
                                        <> Your balance: <strong style={{ color: ACCENT }}>{balance.toLocaleString()}</strong> plays.</>
                                    )}
                                </div>
                            </div>
                            {errors.plays && <div className="mt-2 text-xs text-red-500">{errors.plays}</div>}

                            <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-2">
                                {[minPurchase, 500, 1000, 2500].filter((q) => q >= minPurchase).map((q) => (
                                    <button
                                        key={q} onClick={() => setPlays(q)}
                                        data-testid={`book-plays-quick-${q}`}
                                        className="py-2 rounded-full text-[12px] font-mono-tech uppercase tracking-widest border transition-all"
                                        style={{
                                            borderColor: values.plays === q ? ACCENT : `${INK}25`,
                                            backgroundColor: values.plays === q ? `${ACCENT}12` : "transparent",
                                            color: values.plays === q ? ACCENT : `${INK}99`,
                                        }}
                                    >
                                        {q.toLocaleString()} plays
                                    </button>
                                ))}
                            </div>

                            {tiers.length > 0 && (
                                <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-2">
                                    {tiers.map((t) => {
                                        const active = quote?.tier_id === t.id;
                                        return (
                                            <div
                                                key={t.id}
                                                data-testid={`book-tier-${t.id}`}
                                                className="p-3 rounded-xl border transition-all"
                                                style={{
                                                    borderColor: active ? ACCENT : `${INK}18`,
                                                    backgroundColor: active ? `${ACCENT}0d` : "transparent",
                                                }}
                                            >
                                                <div className="font-display text-sm font-medium" style={{ color: INK }}>{t.label}</div>
                                                <div className="mt-1 font-mono-tech text-[13px]" style={{ color: ACCENT }}>{cents(t.price_per_play_cents)}/play</div>
                                                <div className="text-[10px] mt-1 font-mono-tech" style={{ color: `${INK}70` }}>
                                                    {t.min_plays.toLocaleString()}{t.max_plays ? `–${t.max_plays.toLocaleString()}` : "+"}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </Section>

                        {/* SECTION 5 — Options */}
                        <Section n={5} title="Anything else we should know?" reduce={reduce}>
                            <label className="inline-flex items-center gap-3 text-sm cursor-pointer" style={{ color: INK }}>
                                <input
                                    type="checkbox"
                                    checked={!!values.audio}
                                    onChange={(e) => setValues({ ...values, audio: e.target.checked })}
                                    data-testid="book-audio"
                                    className="w-4 h-4 accent-[#FF5A00]"
                                />
                                <Volume2 size={14} style={{ color: ACCENT }} /> Audio-enabled placement preferred (where available)
                            </label>

                            <div className="mt-6">
                                <label className="block">
                                    <span className="text-[11px] uppercase tracking-[0.28em] mb-2 block" style={{ color: `${INK}70` }}>Notes (optional)</span>
                                    <textarea
                                        rows={4}
                                        value={values.notes}
                                        onChange={(e) => setValues({ ...values, notes: e.target.value })}
                                        data-testid="book-notes"
                                        placeholder="e.g. avoid overlap with a competitor, specific dwell time, artwork already prepared"
                                        className="w-full rounded-2xl border px-4 py-3 text-sm outline-none transition-colors resize-none"
                                        style={{ borderColor: `${INK}25`, color: INK, backgroundColor: "#FFFFFF" }}
                                    />
                                </label>
                            </div>
                        </Section>
                    </div>

                    {/* Sticky right-side pay panel */}
                    <PayPanel
                        quote={quote} values={values} totalCents={totalCents} gstCents={gstCents}
                        grandCents={grandCents} balance={balance} shortfall={shortfall}
                        user={user} submitting={submitting} canSubmit={canSubmit} onSubmit={onSubmit}
                    />
                </div>
            </main>

            {!user && <BookFooter />}
        </div>
        </PortalShell>
    );
}

/* ─────────────────────────── shared bits ──────────────────────────── */
const Section = ({ n, title, children, reduce }) => (
    <motion.section
        initial={reduce ? {} : { opacity: 0, y: 18 }}
        whileInView={reduce ? {} : { opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-40px" }}
        transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
        className="scroll-mt-32"
    >
        <div className="flex items-baseline gap-4 mb-6">
            <div className="font-mono-tech text-sm" style={{ color: ACCENT }}>{String(n).padStart(2, "0")}</div>
            <h2 className="font-display tracking-[-0.02em] text-2xl sm:text-3xl font-medium" style={{ color: INK }}>
                <span dangerouslySetInnerHTML={{ __html: title }} />
            </h2>
        </div>
        <div className="pl-0 md:pl-10">{children}</div>
    </motion.section>
);

const Input = ({ label, value, onChange, icon: Icon, testid, error, type = "text", placeholder }) => (
    <label className="block">
        <span className="text-[11px] uppercase tracking-[0.28em] mb-2 block" style={{ color: `${INK}70` }}>{label}</span>
        <div className="flex items-center gap-2 rounded-2xl border px-4 py-3 transition-colors focus-within:border-[color:var(--acc)]"
            style={{ borderColor: error ? "#EF4444" : `${INK}25`, ["--acc"]: ACCENT }}
        >
            {Icon && <Icon size={15} style={{ color: `${INK}70` }} />}
            <input
                type={type} value={value} onChange={(e) => onChange(e.target.value)}
                data-testid={testid} placeholder={placeholder}
                className="flex-1 bg-transparent outline-none text-sm" style={{ color: INK }}
            />
        </div>
        {error && <div className="mt-1 text-xs text-red-500">{error}</div>}
    </label>
);

const SelectField = ({ label, value, onChange, icon: Icon, testid, options }) => (
    <label className="block">
        <span className="text-[11px] uppercase tracking-[0.28em] mb-2 block" style={{ color: `${INK}70` }}>{label}</span>
        <div className="flex items-center gap-2 rounded-2xl border px-4 py-3 focus-within:border-[color:var(--acc)]"
            style={{ borderColor: `${INK}25`, ["--acc"]: ACCENT }}
        >
            {Icon && <Icon size={15} style={{ color: `${INK}70` }} />}
            <select
                value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}
                className="flex-1 bg-transparent outline-none text-sm cursor-pointer" style={{ color: INK }}
            >
                {options.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
            </select>
        </div>
    </label>
);

const PayPanel = ({ quote, values, totalCents, gstCents, grandCents, balance, shortfall, user, submitting, canSubmit, onSubmit }) => (
    <div className="lg:sticky lg:top-28 h-fit rounded-3xl p-6 lg:p-7" style={{ backgroundColor: INK, color: "#FFFFFF" }} data-testid="book-pay-panel">
        <div className="text-[11px] uppercase tracking-[0.28em] mb-4" style={{ color: "#FFFFFF99" }}>Your reservation</div>

        <Row k="Plays" v={Number(values.plays || 0).toLocaleString()} />
        <Row k="Cities" v={values.cities.join(", ") || "—"} />
        <Row k="Launch" v={values.launch_date || "—"} />
        {values.screen_name && <Row k="Screen" v={values.screen_name} />}

        <div className="h-px my-5" style={{ backgroundColor: "#FFFFFF20" }} />

        <Row k="Rate" v={quote ? `${cents(quote.price_per_play_cents)}/play` : "—"} />
        <Row k="Subtotal" v={dollars(totalCents)} />
        <Row k="GST 10%" v={dollars(gstCents)} />

        <div className="h-px my-5" style={{ backgroundColor: "#FFFFFF20" }} />

        <div className="flex items-baseline justify-between">
            <div className="text-[11px] uppercase tracking-[0.28em]" style={{ color: "#FFFFFF99" }}>Total</div>
            <div className="font-display text-3xl font-medium">{dollars(grandCents)}</div>
        </div>

        {user && balance !== null && (
            <div className="mt-3 text-[12px]" style={{ color: "#FFFFFFB0" }}>
                <Wallet size={12} className="inline mr-1" />
                Balance: <strong>{balance.toLocaleString()}</strong> plays
                {shortfall > 0 && <> · shortfall <strong>{shortfall.toLocaleString()}</strong></>}
            </div>
        )}

        <button
            onClick={onSubmit}
            disabled={!canSubmit || submitting}
            data-testid="book-submit"
            className="group mt-6 w-full inline-flex items-center justify-center gap-2 rounded-full py-4 text-sm font-medium transition-all disabled:opacity-50"
            style={{ backgroundColor: ACCENT, color: "#FFFFFF" }}
        >
            {submitting ? (
                <><Loader2 size={16} className="animate-spin" /> Submitting…</>
            ) : user?.role === "super_admin" ? (
                <><ShieldCheck size={16} /> Confirm — payment bypassed <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" /></>
            ) : user ? (
                shortfall > 0 ? (
                    <><CreditCard size={16} /> Pay {dollars(grandCents)} & confirm <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" /></>
                ) : (
                    <><Play size={14} /> Confirm with balance <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" /></>
                )
            ) : (
                <><CreditCard size={16} /> Book &amp; pay {dollars(grandCents)} <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" /></>
            )}
        </button>

        <div className="mt-5 flex items-start gap-2 text-[12px]" style={{ color: "#FFFFFFB0" }}>
            <ShieldCheck size={14} className="shrink-0 mt-0.5" style={{ color: ACCENT }} />
            <span>
                {user
                    ? "Cancel anytime before your campaign goes live. GST invoice emailed the moment you confirm."
                    : "No account required. You’ll go straight to secure Stripe checkout on the next screen — pay by card, Apple Pay, or Google Pay."}
            </span>
        </div>
    </div>
);

const Row = ({ k, v }) => (
    <div className="flex items-start justify-between py-1.5 text-[13px]">
        <span style={{ color: "#FFFFFF99" }}>{k}</span>
        <span className="text-right ml-4 truncate max-w-[60%]" style={{ color: "#FFFFFF" }}>{v}</span>
    </div>
);

const BookHeader = ({ authed }) => (
    <header className="fixed inset-x-0 top-0 z-40 bg-white/85 backdrop-blur border-b" style={{ borderColor: `${INK}12` }}>
        <div className="mx-auto max-w-6xl px-6 lg:px-10 h-16 flex items-center justify-between">
            <Link to="/" className="flex items-center" data-testid="book-logo">
                <NoxnLogo variant="navy" className="h-5 w-auto" />
            </Link>
            <div className="flex items-center gap-6 text-[13px]">
                <Link to={authed ? "/account" : "/login"} className="hover:underline" style={{ color: `${INK}99` }} data-testid="book-signin">
                    {authed ? "My account" : "Sign in"}
                </Link>
            </div>
        </div>
    </header>
);

const BookFooter = () => (
    <footer className="border-t py-10 mt-16" style={{ borderColor: `${INK}12` }}>
        <div className="mx-auto max-w-6xl px-6 lg:px-10 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 text-[13px]" style={{ color: `${INK}75` }}>
            <div className="flex items-center gap-3">
                <NoxnLogo variant="navy" className="h-4 w-auto" />
                <span>© {new Date().getFullYear()} NOXN</span>
            </div>
            <a href="mailto:noxn@noxn.com.au" className="hover:underline" style={{ color: `${INK}75` }}>noxn@noxn.com.au</a>
        </div>
    </footer>
);
